
import React, { createContext, useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import AttachmentPreviewModal from '../../components/attachments/AttachmentPreviewModal';
import { useAuth } from '../AuthContext';
import { api } from '../../services/api';
import { MOCK_ATTACHMENTS } from '../../data/mockAttachments';
import { filterAttachments, sortAttachments } from './utils';
import { AttachmentContextType, Attachment, FilterOptions, PreviewAttachment, SortOptions } from './types';

export type { Attachment, FilterOptions, SortOptions, AttachmentContextType } from './types';

export const AttachmentContext = createContext<AttachmentContextType | undefined>(undefined);

export { useAttachments } from './useAttachments';

export const AttachmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewAttachment, setPreviewAttachment] = useState<PreviewAttachment | null>(null);
  
  const [filters, setFilters] = useState<FilterOptions>({
    search: '',
    dateRange: {
      start: null,
      end: null,
    },
    sender: '',
    fileType: [],
    sizeRange: {
      min: null,
      max: null,
    },
  });
  
  const [sortOption, setSortOption] = useState<SortOptions>({
    field: 'emailDate',
    direction: 'desc',
  });
  
  const filteredAttachments = useMemo(() => {
    const filtered = filterAttachments(attachments, filters);
    return sortAttachments(filtered, sortOption);
  }, [attachments, filters, sortOption]);
  
  const selectedAttachments = useMemo(() => {
    return Array.from(selected);
  }, [selected]);
  
  useEffect(() => {
    setSelected(new Set());
  }, [filters, sortOption]);
  
  // Auto-fetch attachments when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchAttachments();
    }
  }, [isAuthenticated]);
  
  const fetchAttachments = async () => {
    if (!isAuthenticated) {
      setError(new Error('User is not authenticated'));
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Get the user from localStorage
      const userJson = localStorage.getItem('gmail-attachment-user');
      if (!userJson) {
        throw new Error('User session not found');
      }
      
      const user = JSON.parse(userJson);
      
      // Use the real Gmail API to fetch attachments
      const { data: apiAttachments } = await api.attachments.getAll(user.accessToken);
      
      if (apiAttachments && apiAttachments.length > 0) {
        setAttachments(apiAttachments);
        toast.success(`Loaded ${apiAttachments.length} attachments`);
      } else {
        toast.info('No attachments found in your Gmail account');
        // Fallback to mock data if no real attachments found
        setAttachments(MOCK_ATTACHMENTS);
      }
    } catch (err) {
      console.error('Error fetching attachments:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch attachments'));
      toast.error('Failed to fetch attachments');
      // Fallback to mock data on error
      setAttachments(MOCK_ATTACHMENTS);
    } finally {
      setIsLoading(false);
    }
  };
  
  const selectAttachment = (id: string) => {
    setSelected((prev) => {
      const updated = new Set(prev);
      updated.add(id);
      return updated;
    });
  };
  
  const deselectAttachment = (id: string) => {
    setSelected((prev) => {
      const updated = new Set(prev);
      updated.delete(id);
      return updated;
    });
  };
  
  const toggleAttachmentSelection = (id: string) => {
    setSelected((prev) => {
      const updated = new Set(prev);
      if (updated.has(id)) {
        updated.delete(id);
      } else {
        updated.add(id);
      }
      return updated;
    });
  };
  
  const selectAll = () => {
    const allIds = new Set(filteredAttachments.map((att) => att.id));
    setSelected(allIds);
  };
  
  const deselectAll = () => {
    setSelected(new Set());
  };
  
  const clearSelectedAttachments = () => {
    setSelected(new Set());
  };
  
  const updateFilters = (newFilters: Partial<FilterOptions>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  };
  
  const resetFilters = () => {
    setFilters({
      search: '',
      dateRange: {
        start: null,
        end: null,
      },
      sender: '',
      fileType: [],
      sizeRange: {
        min: null,
        max: null,
      },
    });
  };
  
  const updateSortOption = (newSortOption: Partial<SortOptions>) => {
    setSortOption((prev) => ({
      ...prev,
      ...newSortOption,
    }));
  };
  
  const updateSortOptions = updateSortOption;
  
  const downloadAttachment = async (id: string, preview = false) => {
    try {
      const attachment = attachments.find((att) => att.id === id);
      if (!attachment) {
        throw new Error('Attachment not found');
      }
      
      if (preview) {
        // Get the user token from localStorage
        const userJson = localStorage.getItem('gmail-attachment-user');
        if (!userJson) {
          throw new Error('User session not found');
        }
        const user = JSON.parse(userJson);

        try {
          // Call the API to get the attachment data
          toast.success(`Loading preview: ${attachment.fileName}`);
          const result = await api.attachments.download(id, attachment.emailId, user.accessToken);
          
          if (!result.success) {
            throw new Error('Failed to load file for preview');
          }
          
          // Convert the base64 data to a Blob
          const binaryString = result.data;
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          // Set proper MIME type based on file extension
          const getMimeType = (fileType: string, fileName: string) => {
            const lowerType = fileType.toLowerCase();
            // PDF file
            if (lowerType === 'pdf' || fileName.toLowerCase().endsWith('.pdf')) {
              return 'application/pdf';
            }
            // Image files
            if (['jpg', 'jpeg'].includes(lowerType) || fileName.toLowerCase().match(/\.jpe?g$/)) {
              return 'image/jpeg';
            }
            if (lowerType === 'png' || fileName.toLowerCase().endsWith('.png')) {
              return 'image/png';
            }
            if (lowerType === 'gif' || fileName.toLowerCase().endsWith('.gif')) {
              return 'image/gif';
            }
            // Video files
            if (lowerType === 'mp4' || fileName.toLowerCase().endsWith('.mp4')) {
              return 'video/mp4';
            }
            if (lowerType === 'webm' || fileName.toLowerCase().endsWith('.webm')) {
              return 'video/webm';
            }
            // Audio files
            if (lowerType === 'mp3' || fileName.toLowerCase().endsWith('.mp3')) {
              return 'audio/mpeg';
            }
            if (lowerType === 'wav' || fileName.toLowerCase().endsWith('.wav')) {
              return 'audio/wav';
            }
            
            // Default to binary for unknown types
            return 'application/octet-stream';
          };
          
          const mimeType = getMimeType(attachment.fileType, attachment.fileName);
          console.log('Using MIME type for preview:', mimeType);
          
          // Create blob and get URL for preview
          const blob = new Blob([bytes], { type: mimeType });
          const contentUrl = URL.createObjectURL(blob);
          
          // Set the preview attachment and open the preview modal
          setPreviewAttachment({ ...attachment, contentUrl });
          setPreviewOpen(true);
        } catch (err) {
          console.error('Error preparing preview:', err);
          toast.error(`Failed to prepare preview for ${attachment.fileName}`);
        }
        
        return; // Ensure we return early to prevent download code from executing
      }
      
      toast.success(`Started downloading: ${attachment.fileName}`);
      
      // Get the user token from localStorage
      const userJson = localStorage.getItem('gmail-attachment-user');
      if (!userJson) {
        throw new Error('User session not found');
      }
      const user = JSON.parse(userJson);
      
      // Call the API to get the attachment data
      const result = await api.attachments.download(id, attachment.emailId, user.accessToken);
      
      if (!result.success) {
        throw new Error('Failed to download file');
      }
      
      // Convert the base64 data to a Blob
      const binaryString = result.data;
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // Create blob and download link
      const blob = new Blob([bytes], { type: attachment.fileType });
      const url = URL.createObjectURL(blob);
      
      // Create a download link and trigger it
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = attachment.fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      // Clean up
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
      
      toast.success(`Successfully downloaded: ${attachment.fileName}`);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      toast.error('Failed to download attachment');
    }
  };
  
  const downloadSelected = async () => {
    if (selected.size === 0) {
      toast.error('No attachments selected');
      return;
    }
    
    try {
      const selectedAttachments = attachments.filter((att) => selected.has(att.id));
      
      toast.success(`Started downloading ${selected.size} attachments`);
      
      // Get the user token from localStorage
      const userJson = localStorage.getItem('gmail-attachment-user');
      if (!userJson) {
        throw new Error('User session not found');
      }
      const user = JSON.parse(userJson);
      
      // Download files one at a time
      for (const attachment of selectedAttachments) {
        try {
          // Call the API to get the attachment data
          const result = await api.attachments.download(attachment.id, attachment.emailId, user.accessToken);
          
          if (!result.success) {
            toast.error(`Failed to download ${attachment.fileName}`);
            continue;
          }
          
          // Convert the base64 data to a Blob
          const binaryString = result.data;
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          // Create blob and download link
          const blob = new Blob([bytes], { type: attachment.fileType });
          const url = URL.createObjectURL(blob);
          
          // Create a download link and trigger it
          const downloadLink = document.createElement('a');
          downloadLink.href = url;
          downloadLink.download = attachment.fileName;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          
          // Clean up
          document.body.removeChild(downloadLink);
          URL.revokeObjectURL(url);
          
          // Add a small delay between downloads to prevent browser throttling
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch (err) {
          console.error(`Error downloading ${attachment.fileName}:`, err);
          toast.error(`Failed to download ${attachment.fileName}`);
        }
      }
      
      toast.success(`Successfully downloaded ${selected.size} attachments`);
    } catch (err) {
      console.error('Error downloading selected attachments:', err);
      toast.error('Failed to download attachments');
    }
  };
  
  const bulkDownload = downloadSelected;
  
  const exportCSV = () => {
    toast.success('Exporting attachments to CSV');
  };
  
  const downloadProgress: Record<string, number> = {};
  const currentDownloads: string[] = [];
  
  const value: AttachmentContextType = {
    attachments,
    filteredAttachments,
    isLoading,
    error,
    selected,
    filters,
    sortOption,
    sortOptions: sortOption,
    selectedAttachments,
    previewOpen,
    previewAttachment,
    fetchAttachments,
    selectAttachment,
    deselectAttachment,
    toggleAttachmentSelection,
    selectAll,
    deselectAll,
    clearSelectedAttachments,
    updateFilters,
    resetFilters,
    updateSortOption,
    updateSortOptions,
    downloadSelected,
    downloadAttachment,
    bulkDownload,
    exportCSV,
    downloadProgress,
    currentDownloads,
    setPreviewOpen,
  };
  
  return (
    <AttachmentContext.Provider value={value}>
      {children}
      <AttachmentPreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        attachment={previewAttachment}
      />
    </AttachmentContext.Provider>
  );
};
